import { useState, useEffect } from 'react';
import { TABLE_ROW_DENSITY, TAllTableStates, TColVis } from '@types';
import { useLocalStorage } from '@hooks';

/*   useTableState requires ID for table (table name) as string
     and the tables column data */

export const useTableState = (tableId: string) => {
	const [loaded, setLoaded] = useState(false);
	/*   GLOBAL APP TABLE STATES */
	const [allTableSorts, setAllTableSorts] = useState({});
	const [allTableColumnVis, setAllTableColumnVis] = useState({});
	const [allTablePins, setAllTablePins] = useState({});
	const [allTableFilters, setAllTableFilters] = useState({});
	const [allTableColumnOrder, setAllTableColumnOrder] = useState({});
	const [allTableColumnWidths, setAllTableColumnWidths] = useState({});
	const [allTableZebraStripes, setAllTableZebraStripes] = useState({});
	const [allTableRowDensity, setAllTableRowDensity] = useLocalStorage<{[key: string]: string}>('@ofloadTableRowDensity', {});

	/*   SET & RETURN INTERNAL STATES */
	const [sortModel, setSortingModel] = useState([]);
	const [columnVisibility, setColumnVisibilityModel] = useState<TColVis>({});
	const [tableFilters, setTableFiltersModel] = useState([]);
	const [tableColumnOrder, setTableColumnOrder] = useState([]);
	const [colWidths, setTableColWidths] = useState({});
	const [showZebraStripes, setShowZebraStripes] = useState(true);
	const [pinColumns, setPinColumnsModel] = useState({
		left: [],
		right: []
	});

	/*   useTableState Hook Methods */
	const rowDensity = allTableRowDensity?.[tableId] ?? TABLE_ROW_DENSITY.REGULAR;
	const setRowDensity = (value: TABLE_ROW_DENSITY) => {
		setAllTableRowDensity((all: TAllTableStates<string>) => ({
			...all,
			[tableId]: value,
		}));
	};
	const clearRowDensity = () => {
		setAllTableRowDensity((all: TAllTableStates<string>) => {
			const { [tableId]: currentTable, ...withoutCurrentTable } = all;
			return withoutCurrentTable;
		});
	};

	const setZebraStripes = (stripesOn: boolean) => {
		const clone: TAllTableStates<boolean> = { ...allTableZebraStripes };
		clone[tableId] = stripesOn;
		setShowZebraStripes(stripesOn);
		localStorage.setItem('@ofloadTableZebraStripes', JSON.stringify(clone));
		setAllTableZebraStripes(clone);
	}
	const setColWidths = (columns:any) => {
		const cols = columns();
		const clone:any = { ...allTableColumnWidths };
		const destructure = Object.entries(cols);
		if (clone[tableId] === undefined) {
			clone[tableId] = {};
		}
		clone[tableId][destructure[0][0]] = destructure[0][1]; // eslint-disable-line
		setTableColWidths(clone[tableId]);
		localStorage.setItem('@ofloadTableColWidths', JSON.stringify(clone));
		setAllTableColumnWidths(clone);
	}
	const setColumnOrder = (columns:any) => {
		const clone:any = { ...allTableColumnOrder };
		clone[tableId] = columns;
		setTableColumnOrder(columns);
		localStorage.setItem('@ofloadTableColOrder', JSON.stringify(clone));
		setAllTableColumnOrder(clone);
	}
	const setColumnPins = (pins:any) => {
		const clone:any = { ...allTablePins };
		setPinColumnsModel(pins);
		if (typeof pins !== 'function') {
			clone[tableId] = pins;
			localStorage.setItem('@ofloadTableColPins', JSON.stringify(clone));
			setAllTablePins(clone);
		}
	}
	const setPinColumns = (pins:any) => {
		const clone:any = { ...allTablePins };
		clone[tableId] = pins;
		setPinColumnsModel(pins);
		localStorage.setItem('@ofloadTableColPins', JSON.stringify(clone));
		setAllTablePins(clone);
	}
	const setColumnVisibility = (columns:any) => {
		const clone: TAllTableStates<TColVis> = { ...allTableColumnVis };
		clone[tableId] = columns;
		setColumnVisibilityModel(columns);
		localStorage.setItem('@ofloadTableColVisibility', JSON.stringify(clone));
		setAllTableColumnVis(clone);
	}
	const setSortModel = (newSortModel:any) => {
		const clone:any = { ...allTableSorts };
		clone[tableId] = newSortModel;
		setSortingModel(newSortModel);
		localStorage.setItem('@ofloadTableSortOrder', JSON.stringify(clone));
		setAllTableSorts(clone);
	}
	const setTableFilters = (filters:any) => {
		const cloned:any = { ...allTableFilters }
		cloned[tableId] = filters;
		localStorage.setItem('@ofloadTableFilters', JSON.stringify(cloned));
		setTableFiltersModel(filters);
	}
	const saveActiveFilter = (tableID:string, filterID:string) => {
		const key = tableID ?? window.location.pathname
		if (filterID) {
			localStorage.setItem(`@${key}-active-filter`, filterID)
		}
	}
	const clearActiveFilter = (tableID:string) => {
		const key = tableID ?? window.location.pathname
		localStorage.removeItem(`@${key}-active-filter`)
	}
	const getActiveFilter = (tableID:string): null|string => {
		const key = tableID ?? window.location.pathname
		return localStorage.getItem(`@${key}-active-filter`)
	}
	const clearTableState = (options:any = null) => {
		let exceptions
		if (options && options.exceptions) exceptions = options.exceptions; // eslint-disable-line
		const sortsClone:any = { ...allTableSorts };
		const visClone: TAllTableStates<TColVis> = { ...allTableColumnVis };
		const pinsClone:any = { ...allTablePins };
		const filtersClone:any = { ...allTableFilters };
		const colWidthClone:any = { ...allTableColumnWidths };
		const colOrderClone:any = { ...allTableColumnOrder };
		const zebraStripesClone: TAllTableStates<boolean> = { ...allTableZebraStripes };
		if (!exceptions || !exceptions?.sort) delete sortsClone[tableId];
		if (!exceptions || !exceptions?.visible) delete visClone[tableId];
		if (!exceptions || !exceptions?.pins) delete pinsClone[tableId];
		if (!exceptions || !exceptions?.filter) delete filtersClone[tableId];
		if (!exceptions || !exceptions?.width) delete colWidthClone[tableId];
		if (!exceptions || !exceptions?.order) delete colOrderClone[tableId];
		if (!exceptions || !exceptions?.zebraStripes) delete zebraStripesClone[tableId];
		localStorage.setItem('@ofloadTableSortOrder', JSON.stringify(sortsClone));
		localStorage.setItem('@ofloadTableColVisibility', JSON.stringify(visClone));
		localStorage.setItem('@ofloadTableColPins', JSON.stringify(pinsClone));
		localStorage.setItem('@ofloadTableFilters', JSON.stringify(filtersClone));
		localStorage.setItem('@ofloadTableColWidths', JSON.stringify(colWidthClone));
		localStorage.setItem('@ofloadTableColOrder', JSON.stringify(colOrderClone));
		localStorage.setItem('@ofloadTableZebraStripes', JSON.stringify(zebraStripesClone));
		if (!exceptions || !exceptions?.sort) setSortingModel([]);
		if (!exceptions || !exceptions?.visible) setColumnVisibilityModel({});
		if (!exceptions || !exceptions?.filter) setTableFiltersModel([]);
		if (!exceptions || !exceptions?.pins) {
			setPinColumnsModel({
				left: [],
				right: []
			});
		}
		if (!exceptions || !exceptions?.density) clearRowDensity();
		if (!exceptions || !exceptions?.width) setTableColWidths({});
		if (!exceptions || !exceptions?.order) setTableColumnOrder([]);
		if (!exceptions || !exceptions?.zebraStripes) setShowZebraStripes(true);
	}

	/*   INIT & SETUP... */
	useEffect(() => {
		const getFromStorage = () => {
			const savedSortModel = JSON.parse(localStorage.getItem('@ofloadTableSortOrder') ?? '{}');
			const savedColVis = JSON.parse(localStorage.getItem('@ofloadTableColVisibility') ?? '{}');
			const savedPins = JSON.parse(localStorage.getItem('@ofloadTableColPins') ?? '{}');
			const savedFilters = JSON.parse(localStorage.getItem('@ofloadTableFilters') ?? '{}');
			const savedColOrder = JSON.parse(localStorage.getItem('@ofloadTableColOrder') ?? '{}');
			const savedColWidths = JSON.parse(localStorage.getItem('@ofloadTableColWidths') ?? '{}');
			const savedZebraStripes = JSON.parse(localStorage.getItem('@ofloadTableZebraStripes') ?? '{}');
			setAllTableSorts(savedSortModel);
			setAllTableColumnVis(savedColVis);
			setAllTablePins(savedPins);
			setAllTableFilters(savedFilters);
			setAllTableColumnOrder(savedColOrder);
			setAllTableColumnWidths(savedColWidths);
			setAllTableZebraStripes(savedZebraStripes);
			if (savedSortModel[tableId]) {
				setSortingModel(savedSortModel[tableId]);
			}
			if (savedColVis[tableId]) {
				setColumnVisibilityModel(savedColVis[tableId]);
			}
			if (savedPins[tableId]) {
				setPinColumnsModel(savedPins[tableId]);
			}
			if (savedFilters[tableId]) {
				setTableFiltersModel(savedFilters[tableId]);
			}
			if (savedColOrder[tableId]) {
				setTableColumnOrder(savedColOrder[tableId]);
			}
			if (savedColWidths[tableId]) {
				setTableColWidths(savedColWidths[tableId]);
			}
			if (savedZebraStripes[tableId] !== undefined) {
				setShowZebraStripes(savedZebraStripes[tableId]);
			}
			setLoaded(true);
		}
		getFromStorage()
	}, [tableId]);

	return {
		clearTableState,
		setSortModel,
		sortModel,
		columnVisibility,
		setColumnVisibility,
		pinColumns,
		setPinColumns,
		setColumnPins,
		tableFilters,
		setTableFilters,
		tableColumnOrder,
		setColumnOrder,
		colWidths,
		setColWidths,
		rowDensity,
		setRowDensity,
		loaded,
		saveActiveFilter,
		clearActiveFilter,
		getActiveFilter,
		showZebraStripes,
		setZebraStripes,
	};
}
